import { action, observable, flow, makeObservable } from 'mobx';
import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import { ACH_TRANSFER, APPROVED_ACH, APPROVED_WIRE, CounterPartyExpensesTab, PayableReceivablesTypes, VENDOR_PAYMENT_TRANSACTION, WIRE_TRANSFER } from '../types/types';
import { SpecialServicingBaseStore } from './specialServicingBaseStore';
import { Option } from '@roc/ui/utils';
import { downloadDocument } from '@roc/feature-utils';

export class VendorPaymentDashboardStore extends SpecialServicingBaseStore {
  vendorOptions: Option[] = [];
  detailsModalOpen: boolean;
  selectedRecord: any;
  payableDetailsGridStore: GridStore;
  totalPayablesAmount: number;

  constructor(globalStore: GlobalStore) {
    super(globalStore);
    this.tab = CounterPartyExpensesTab.PENDING_VENDOR_PAYMENT;
    this.payableDetailsGridStore = new GridStore(
      () => this.fetchPayableDetails(),
      null,
      50
    );

    makeObservable(this, {
      fetchEntityNames: flow,
      performAction: flow,
      performActions: flow,
      vendorOptions: observable,
      detailsModalOpen: observable,
      openModal: action,
      closeModal: action,
      selectedRecord: observable,
      removeFromVendorPayment: flow,
      totalPayablesAmount: observable,
      performActionsWithParam: flow,
      performActionWithParam: flow,
      downloadVendorPaymentDocuments: flow,
      exportVendorPaymentInvoicesData: flow,
    });
  };

  private async fetchPayableDetails() {
    try {
      const filters = {
        ...this.payableDetailsGridStore.gridData.meta.filters
      };
      const response: ApiResponse = await this.service.getCounterPartyExpensesData(
        CounterPartyExpensesTab.VENDOR_PAYMENT_PAYABLES, //this.tab,
        this.selectedRecord?.recordId.toString(),
        this.payableDetailsGridStore.gridData.meta.pageNumber,
        this.payableDetailsGridStore.gridData.meta.pageSize,
        this.payableDetailsGridStore.gridData.meta.sortDir,
        this.payableDetailsGridStore.gridData.meta.sortBy,
        filters,
        this.payableDetailsGridStore.gridData.meta.dropdownFilters,
      );
      this.totalPayablesAmount = parseFloat(response.data.data.rows.reduce((total, payable) => total + payable.amount, 0).toFixed(2));
      return response;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching counter party expenses report.',
      });
    }
  };

  openModal(vendorPayment: any) {
    this.detailsModalOpen = true;
    this.selectedRecord = vendorPayment;
  }

  closeModal() {
    this.detailsModalOpen = false;
  }

  *fetchEntityNames() {
    try {
      const response: ApiResponse = yield this.service.getVendorsNames();
      this.entityList = response.data.data.map(value => (
        { label: value.vendorName, value: value.companyId }
      ));
      this.vendorOptions = this.entityList.map(value => (
        { label: value.label, value: value.label }
      ));
    }
    catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching vendor names.',
      });
    }
  };


  reset() {
    this.tab = CounterPartyExpensesTab.PENDING_VENDOR_PAYMENT;
    this.counterPartyExpenseGridStore.reset();
  };

  *performActions(status: string) {
    throw Error('Not Implemented!');
  }

  *performActionsWithParam(status: string, param: string) {
    const statusType = this.getStatusType(status, ACH_TRANSFER);
    try {
      const response = yield this.service.performVendorPaymentAction(this.recordsIds, statusType, param);
      if (response?.data?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Status updated successfully.'
        });
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: response?.data?.responseMessage,
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating status.',
      });
    }
  }

  *performAction(id: number, status: string) {
    throw Error('Not Implemented!');
  }

  *performActionWithParam(status: string, param: string, transferType: string) {
    const idList: number[] = [this.selectedRecord?.recordId];
    const statusType = this.getStatusType(status, transferType);
    try {
      const response = yield this.service.performVendorPaymentAction(idList, statusType, param);
      if (response?.data?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Status updated successfully.'
        });
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: response?.data?.responseMessage,
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating status.',
      });
    }
  }

  getStatusType(status: string, transferType: string): string {
    return status === PayableReceivablesTypes.APPROVED ?
        transferType === WIRE_TRANSFER ? APPROVED_WIRE : APPROVED_ACH
        : status;
  }

  *removeFromVendorPayment(payableId: number) {
    try {
      const response = yield this.service.removeFromVendorPayment(payableId);
      if (response.data.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Status updated successfully.'
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating status.',
      });
    }
  }

  *downloadVendorPaymentDocuments(vendorPaymentIds: number[]) {
    try {
      const response: ApiResponse = yield this.service.downloadVendorPaymentDocuments(vendorPaymentIds);
      downloadDocument(response.data, response.headers, 'download', `${VENDOR_PAYMENT_TRANSACTION}_DOCUMENT_BUNDLE.zip`);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while downloading vendor payment document bundle.',
      });
    }
  };

  *exportVendorPaymentInvoicesData(vendorPaymentId: string) {
    try {
      const response: ApiResponse = yield this.service.exportServicingDashboardData(
        VENDOR_PAYMENT_TRANSACTION,
        vendorPaymentId,
        null,
        null
      );
      downloadDocument(response.data, response.headers, 'download', `${VENDOR_PAYMENT_TRANSACTION}_REPORT.xlsx`);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while exporting vendor Payment invoices data.',
      });
    }
  };

}
